import { useEffect, useRef, useState, type ReactNode } from 'react';
import { formatINR, formatQuantity, parseRupees } from '@shared/money';
import { formatLocalDate, formatLocalDateTime } from '@shared/time';
import { ApiError } from '@shared/client';

export function Money({ paise, className }: { paise?: string | null; className?: string }) {
  if (paise === undefined || paise === null) return <span className={className}>—</span>;
  return <span className={`mono ${className ?? ''}`}>{formatINR(paise)}</span>;
}

export const inr = (p?: string | null) => (p === undefined || p === null ? '—' : formatINR(p));
export const litres = (q?: string | null, unit = 'L') => (q ? formatQuantity(q, unit) : '—');
export const dt = (d?: string | null) => (d ? formatLocalDateTime(d) : '—');
export const day = (d?: string | null) => (d ? formatLocalDate(d) : '—');

export function Stat({ label, value, tone, big }: { label: string; value: ReactNode; tone?: 'good' | 'bad'; big?: boolean }) {
  return (
    <div className={`stat ${tone ?? ''} ${big ? 'big' : ''}`}>
      <span className="label">{label}</span>
      <span className="value">{value}</span>
    </div>
  );
}

export function Badge({ children, tone }: { children: ReactNode; tone?: 'ok' | 'warn' | 'bad' | 'info' }) {
  return <span className={`badge ${tone ?? ''}`}>{children}</span>;
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div role="status" aria-live="polite">
      <div className="spinner" />
      <p className="empty">{label}</p>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (!error) return null;
  const e = error as ApiError;
  const dup = e.code === 'IDEMPOTENCY_KEY_REUSED' || e.code === 'TOKEN_ALREADY_USED' || e.code === 'PAYMENT_ALREADY_CONFIRMED';
  return (
    <div className={`alert ${dup ? 'warn' : 'bad'}`} role="alert">
      <strong>{e.message || 'Something went wrong.'}</strong>
      {e.code === 'NETWORK' ? null : e.code && e.code !== 'ERROR' ? <div><small>Code: {e.code}</small></div> : null}
      {onRetry ? (
        <div style={{ marginTop: '.5rem' }}>
          <button className="btn small" onClick={onRetry}>Try again</button>
        </div>
      ) : null}
    </div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint ? <span className="hint">{hint}</span> : null}
    </label>
  );
}

/** Keeps the 10 local digits of an Indian mobile, also when pasted as +91 or 0-prefixed. */
export function mobileDigits(raw: string): string {
  let d = raw.replace(/\D/g, '');
  if (d.length > 10 && d.startsWith('91')) d = d.slice(2);
  else if (d.length > 10 && d.startsWith('0')) d = d.slice(1);
  return d.slice(0, 10);
}

/** Indian mobile number: +91 is shown, only the 10 digits are typed. */
export function MobileInput({ value, onChange, autoFocus, required }: { value: string; onChange: (digits: string) => void; autoFocus?: boolean; required?: boolean }) {
  return (
    <span className="mobile-input">
      <span className="mobile-prefix" aria-hidden="true">+91</span>
      <input
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        placeholder="9876543210"
        pattern="[6-9][0-9]{9}"
        title="10-digit mobile number"
        value={value}
        onChange={(e) => onChange(mobileDigits(e.target.value))}
        autoFocus={autoFocus}
        required={required}
      />
    </span>
  );
}

/** Rupee input that hands back integer paise as a string (or null when empty/invalid). */
export function RupeeInput({ value, onChange, placeholder, autoFocus, id, ariaLabel, disabled }: { value: string; onChange: (raw: string) => void; placeholder?: string; autoFocus?: boolean; id?: string; ariaLabel?: string; disabled?: boolean }) {
  return <input id={id} aria-label={ariaLabel} disabled={disabled} inputMode="decimal" value={value} placeholder={placeholder ?? '0.00'} autoFocus={autoFocus} onChange={(e) => onChange(e.target.value)} />;
}

export function rupeesToPaise(raw: string): string | null {
  if (!raw.trim()) return null;
  try {
    const p = parseRupees(raw);
    return p >= 0n ? p.toString() : null;
  } catch {
    return null;
  }
}

export function Dialog({ open, title, onClose, children }: { open: boolean; title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    // Inline close callbacks change while typing. Focus only when opening,
    // preferring a form field over the header's Close button.
    const target = ref.current?.querySelector<HTMLElement>('input:not([type="hidden"]):not(:disabled),select:not(:disabled),textarea:not(:disabled)')
      ?? ref.current?.querySelector<HTMLElement>('button:not(:disabled)');
    target?.focus();
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="dialog" role="dialog" aria-modal="true" aria-label={title} ref={ref} onClick={(e) => e.stopPropagation()}>
        <div className="row between">
          <h2>{title}</h2>
          <button className="btn ghost small" onClick={onClose} aria-label="Close">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** "Check before you confirm" summary for every money-changing action. */
export function ConfirmDialog({
  open, title, lines, confirmLabel, danger, busy, confirmDisabled, error, onConfirm, onClose, children,
}: {
  open: boolean;
  title: string;
  lines: [string, ReactNode][];
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  confirmDisabled?: boolean;
  error?: unknown;
  onConfirm: () => void;
  onClose: () => void;
  children?: ReactNode;
}) {
  return (
    <Dialog open={open} title={title} onClose={onClose}>
      <div className="summary card">
        <dl>
          {lines.map(([k, v]) => (
            <FragmentRow key={k} k={k} v={v} />
          ))}
        </dl>
      </div>
      {children}
      <ErrorBox error={error} />
      <div className="row">
        <button className={`btn ${danger ? 'danger' : 'primary'}`} disabled={busy || confirmDisabled} onClick={onConfirm}>
          {busy ? 'Please wait…' : confirmLabel}
        </button>
        <button className="btn" onClick={onClose} disabled={busy}>Go back</button>
      </div>
    </Dialog>
  );
}

function FragmentRow({ k, v }: { k: string; v: ReactNode }) {
  return (
    <>
      <dt>{k}</dt>
      <dd>{v}</dd>
    </>
  );
}

export function useBusy() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const run = async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(e);
      return undefined;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
}

export function statusTone(status: string): 'ok' | 'warn' | 'bad' | 'info' | undefined {
  if (['APPROVED', 'ACTIVE', 'CONFIRMED', 'ACKNOWLEDGED', 'ACKNOWLEDGED_BY_ACCOUNTS', 'POSTED', 'RESOLVED', 'VERIFIED', 'FULFILLED'].includes(status)) return 'ok';
  if (['REQUESTED', 'PENDING', 'PENDING_CONFIRMATION', 'PENDING_BUYER_APPROVAL', 'CLAIMED', 'OPEN', 'TERMS_PROPOSED', 'PENDING_OPENING_BALANCE', 'UNVERIFIED', 'DUE_SOON', 'DUE_TODAY'].includes(status)) return 'warn';
  if (['REJECTED', 'REVOKED', 'EXPIRED', 'REVERSED', 'CANCELLED', 'OVERDUE_1_7', 'OVERDUE_8_30', 'OVERDUE_30_PLUS'].includes(status)) return 'bad';
  return 'info';
}

export function today(): string {
  return new Date(Date.now() + 330 * 60_000).toISOString().slice(0, 10);
}
